import { query } from 'bitecs';
import { AnnouncerAudioEngine } from '@src/audio/AnnouncerAudioEngine';
import { MusicEngine } from '@src/audio/MusicEngine';
import { FLOOR_TRACKS, MusicTracks, type TrackId } from '@src/audio/MusicTracks';
import { CrowdBed } from '@src/audio/CrowdBed';
import { Sfx, SfxId, BOSS_DEATH_SAMPLE_IDS, ACTION_SFX_SAMPLE_IDS, CROWD_SFX_SAMPLE_IDS } from '@src/audio/Sfx';
import { SoundBusManager } from '@src/audio/SoundBusManager';
import { Config } from '@src/core/Config';
import { BossImpactKind, GameEvent } from '@src/core/EventQueue';
import { PoolKind } from '@src/core/Pools';
import { EnemyKind, Health, Player, Weapon } from '@src/ecs/components';
import type { GameSystem } from '@src/ecs/System';
import { BossState, RoomPhase, type GameContext } from '@src/ecs/world';
import { allClipIds } from '@src/game/AnnouncerLines';
import { PrizeKind } from '@src/ecs/archetypes';
import { DoorKind } from '@src/game/arena/RoomLayout';
import { BossBody, bossDef, BossPattern } from '@src/game/BossDefs';
import { BossKind } from '@src/ecs/components';
import { floorThemeDef } from '@src/game/arena/FloorThemes';
import { floorMod } from '@src/game/FloorModifiers';
import { newThreatAnnouncerGroup } from '@src/game/SpawnRoster';
import { FirePattern, WeaponId, weaponDef } from '@src/game/weapons/WeaponDefs';

/** Signature entrance cue per champion; archmages keep the arcane BossArrive. */
const BOSS_ARRIVE_SFX: Partial<Record<BossKind, SfxId>> = {
  [BossKind.Warlord]: SfxId.BossArriveWarlord,
  [BossKind.SkullKing]: SfxId.BossArriveSkullKing,
  [BossKind.Colossus]: SfxId.BossArriveColossus,
  [BossKind.Matriarch]: SfxId.BossArriveMatriarch,
  [BossKind.Sentinel]: SfxId.BossArriveSentinel,
};

/** Cinematic ElevenLabs death stem per boss; Magus→lich, Sentinel→colossus. */
const BOSS_DEATH_SAMPLE: Partial<Record<BossKind, string>> = {
  [BossKind.Lich]: 'boss_death_lich',
  [BossKind.Magus]: 'boss_death_lich',
  [BossKind.Druid]: 'boss_death_druid',
  [BossKind.Warlord]: 'boss_death_warlord',
  [BossKind.SkullKing]: 'boss_death_skullking',
  [BossKind.Colossus]: 'boss_death_colossus',
  [BossKind.Sentinel]: 'boss_death_colossus',
  [BossKind.Matriarch]: 'boss_death_matriarch',
  // Later champions borrow the stem of the body they are built from.
  [BossKind.Hexweaver]: 'boss_death_lich',
  [BossKind.Ashmaw]: 'boss_death_skullking',
  [BossKind.Bride]: 'boss_death_matriarch',
};

/** Which room-entrance line a door kind gets (Combat / Exit fall through to room_enter). */
const ROOM_ENTER_LINES: Partial<Record<DoorKind, string>> = {
  [DoorKind.Treasure]: 'treasure_room',
  [DoorKind.Hazard]: 'hazard_room',
  [DoorKind.Arena]: 'arena_room',
  [DoorKind.Gauntlet]: 'gauntlet_room',
  [DoorKind.Sanctum]: 'sanctum_room',
};

/** Each pattern family gets its own charge-up so the ear learns what is coming. */
function telegraphSfx(pattern: number): SfxId {
  switch (pattern as BossPattern) {
    case BossPattern.Aimed:
    case BossPattern.Rubble:
      return SfxId.BossTelegraphAimed;
    case BossPattern.Radial:
    case BossPattern.RingGap:
      return SfxId.BossTelegraphRadial;
    case BossPattern.Orbit:
    case BossPattern.Crown:
    case BossPattern.Wisps:
    case BossPattern.Summon:
      return SfxId.BossTelegraphOrb;
    case BossPattern.Beam:
      return SfxId.BossTelegraphBeam;
    case BossPattern.Stomp:
    case BossPattern.Charge:
      return SfxId.BossTelegraphHeavy;
    case BossPattern.Screech:
      return SfxId.BossTelegraphInhale;
    case BossPattern.Acid:
      return SfxId.BossTelegraphGurgle;
    default:
      return SfxId.BossTelegraph;
  }
}

/** Hurt take by body material. */
function hurtSfx(kind: number): SfxId {
  switch (bossDef(kind).body) {
    case BossBody.Stone:
      return SfxId.BossHurtStone;
    case BossBody.Bone:
      return SfxId.BossHurtBone;
    case BossBody.Goo:
      return SfxId.BossHurtGoo;
    case BossBody.Leather:
      return SfxId.BossHurtLeather;
    default:
      return SfxId.BossHurt;
  }
}

/** Death crescendo by body material (archmages keep the arcane one). */
function deathSfx(kind: number): SfxId {
  switch (bossDef(kind).body) {
    case BossBody.Stone:
      return SfxId.BossDeathStone;
    case BossBody.Bone:
      return SfxId.BossDeathBone;
    case BossBody.Goo:
      return SfxId.BossDeathGoo;
    case BossBody.Leather:
      return SfxId.BossDeathLeather;
    default:
      return SfxId.BossDeath;
  }
}

const PRIZE_LINES: Readonly<Record<PrizeKind, string>> = {
  [PrizeKind.Cabbage]: 'prize_cabbage',
  [PrizeKind.Hat]: 'prize_hat',
  [PrizeKind.Chalice]: 'prize_chalice',
  [PrizeKind.GoldChest]: 'prize_gold_chest',
  [PrizeKind.Spellbook]: 'prize_spellbook',
  [PrizeKind.CrystalBall]: 'prize_crystal_ball',
  [PrizeKind.Potion]: 'prize_potion',
  [PrizeKind.Hourglass]: 'prize_hourglass',
};
const IDLE_HECKLES_PER_ROOM = 1;
/** Room-clear arp is ~0.55s; winch starts as that sting lands. */
const GATE_OPEN_DELAY = 0.55;
const PRIZE_AFTER_CLEAR = 0.7;
/** Kill taunts land every N kills, N re-rolled in this range so they never feel metronomic. */
const KILL_TAUNT_MIN = 24;
const KILL_TAUNT_MAX = 42;
const GOLEM_LINE_CHANCE = 0.22;
const STREAK_LINE_CHANCE = 0.35;
const HURT_LINE_CHANCE = 0.28;
const HEAL_LINE_CHANCE = 0.4;
const MINE_LINE_CHANCE = 0.32;
const PICKUP_LINE_CHANCE = 0.45;
const PRIZE_LINE_CHANCE = 0.4;
const PORTAL_LINE_CHANCE = 0.4;
const BUFF_LINE_CHANCE = 0.35;
const EXPIRED_LINE_CHANCE = 0.4;
const STREAK_TIER2_COMBO = 40;
const STREAK_TIER3_COMBO = 80;

function rollKillTaunt(): number {
  return KILL_TAUNT_MIN + Math.floor(Math.random() * (KILL_TAUNT_MAX - KILL_TAUNT_MIN + 1));
}

/**
 * Presentation system that turns game events into Vaelen lines, SFX and music
 * intensity. Everything routes through SoundBusManager.
 */
export class AudioDirector implements GameSystem {
  readonly buses: SoundBusManager;
  readonly announcer: AnnouncerAudioEngine;
  readonly sfx: Sfx;
  readonly crowd: CrowdBed;
  readonly music: MusicEngine;
  /** Streamed soundtrack (ElevenLabs renders); the procedural engine covers any missing file. */
  readonly tracks: MusicTracks;
  private ctx!: GameContext;
  private inArena = false;
  /** Confirm / intro handoff: playMenuMusic must not start a late menu.mp3. */
  private leftMenu = false;
  /** Ignore Shot/Kill/beam/etc. after a clear or death until the next RoomEnter. */
  private combatMuted = false;
  /** Floor-clear / death: do not start the next streamed track until RoomEnter. */
  private musicHeld = false;
  /** Player is down; arena beds stay parked until silenceArena + the next run. */
  private silenced = false;
  /** Pause menu: combat SFX suspended, music ducked; sim ticks are frozen separately. */
  private audioPaused = false;
  private prePauseTrackLevel = 1;
  /** Sponsor break: arena beds fully muted so only commercial / bumper audio plays. */
  private commercialBreak = false;
  private preCommercialCombatMuted = false;
  /** Death cinematic replaces the mop-line death VO. */
  private suppressDeathLine = false;
  private welcomed = false;
  private idleHeckles = 0;
  private nearDeathArmed = true;
  private wasLowPower = false;
  private lowPowerPulseTimer = 0;
  private lastClearStingAt = -99;
  /**
   * Boss death: music stays parked so the crescendo is the only bed. Held until
   * RoomEnter; `musicHeld` is the real lock, this just covers the death window.
   */
  private bossQuietUntil = 0;
  private killsSinceTaunt = 0;
  private nextTauntAt = rollKillTaunt();

  constructor() {
    this.buses = new SoundBusManager();
    this.announcer = new AnnouncerAudioEngine(this.buses);
    this.sfx = new Sfx(this.buses);
    this.crowd = new CrowdBed(this.buses, this.sfx);
    this.music = new MusicEngine(this.buses);
    this.tracks = new MusicTracks(this.buses);
    this.tracks.preload(['menu', ...FLOOR_TRACKS, 'boss']);
    this.sfx.preload([...BOSS_DEATH_SAMPLE_IDS, ...ACTION_SFX_SAMPLE_IDS, ...CROWD_SFX_SAMPLE_IDS]);
    this.crowd.preload();
  }

  init(ctx: GameContext): void {
    this.ctx = ctx;
    this.announcer.preload(allClipIds());
  }

  /**
   * Start-screen music, called on the first user gesture. The procedural theme
   * starts at once so there is sound immediately; if menu.mp3 decodes it fades in
   * over the top and the procedural bed steps aside.
   */
  async playMenuMusic(): Promise<void> {
    await this.buses.resume();
    if (this.inArena || this.leftMenu) return;
    this.music.startMenu();
    this.crowd.setMurmur(0.11, 1.1);
    const ok = await this.tracks.ready('menu');
    if (!ok || this.inArena || this.leftMenu || this.music.isIntro()) return;
    if (this.tracks.play('menu', 2.2)) this.music.stopMenu(2.4);
  }

  /**
   * Title confirm: kill streamed + procedural menu, cut Vaelen's pick/idle line,
   * and fade leftover MenuMove tails. Idempotent; startIntroBed calls this first.
   */
  leaveMenu(): void {
    this.leftMenu = true;
    try {
      this.tracks.stop(0.32);
    } catch {
      // streamed bed optional
    }
    try {
      this.music.stopMenu(0.32);
    } catch {
      // procedural bed optional
    }
    try {
      this.announcer.interrupt(true);
    } catch {
      //
    }
    try {
      this.sfx.stopVoices(0.1);
    } catch {
      //
    }
  }

  /** Menu -> cinematic: short menu fade, then the procedural intro bed. */
  startIntroBed(): void {
    this.leaveMenu();
    this.music.startIntro();
    this.crowd.setMurmur(0.14, 0.8);
  }

  /**
   * Kill in-flight combat SFX (generation bus + beam). When `music` is true
   * (default), also park streamed + procedural battle beds within ~200ms.
   */
  stopCombat(opts?: { music?: boolean }): void {
    this.combatMuted = true;
    this.wasLowPower = false;
    this.lowPowerPulseTimer = 0;
    this.sfx.stopCombat(0.16);
    this.crowd.setMurmur(this.silenced ? 0 : 0.1, 0.35);
    if (opts?.music !== false) {
      this.musicHeld = true;
      this.bossQuietUntil = 0;
      this.tracks.stop(0.18);
      this.music.stop(0.18);
    }
  }

  /**
   * Hard silence before a restart or a return to title: no leftover loops,
   * no queued fight chatter. Next RoomEnter starts a clean arena bed.
   */
  silenceArena(): void {
    this.silenced = false;
    this.audioPaused = false;
    this.commercialBreak = false;
    this.combatMuted = true;
    this.wasLowPower = false;
    this.lowPowerPulseTimer = 0;
    this.musicHeld = true;
    this.bossQuietUntil = 0;
    this.sfx.stopCombat(0.1);
    this.crowd.stopAll(0.12);
    this.music.stop(0.12);
    this.tracks.stop(0.1);
    this.announcer.interrupt(true);
  }

  /**
   * Pause menu: suspend combat SFX / beam, duck music. Announcer lines already
   * in flight may finish; new combat cues stay quiet until resume.
   */
  pauseCombat(): void {
    if (this.audioPaused || this.silenced || this.commercialBreak) return;
    this.audioPaused = true;
    this.prePauseTrackLevel = 1;
    this.sfx.stopCombat(0.1);
    this.crowd.setMurmur(0.05, 0.25);
    this.tracks.setLevel(0.35, 0.25);
    // Only duck the procedural bed when it is the audible layer.
    if (!this.tracks.playing()) this.music.setDuck(0.4, 0.25);
  }

  /** Undo pauseCombat. No-op if silenceArena parked everything for a restart. */
  resumeCombat(): void {
    if (!this.audioPaused) return;
    this.audioPaused = false;
    if (this.silenced || this.musicHeld || this.commercialBreak) return;
    this.crowd.setMurmur(this.inArena ? 0.16 : 0.11, 0.4);
    this.tracks.setLevel(this.prePauseTrackLevel, 0.3);
    // Restore procedural mute/unmute from the streamed layer (setDuck must not fight this).
    this.music.setStreamed(this.tracks.playing() !== null);
  }

  /**
   * Sponsor commercial: hard-mute arena music / ambient beds and combat SFX so
   * only the bumper voice, jingle, and product VO remain. Leaves the SFX +
   * announcer buses open for broadcast stingers and Vaelen.
   */
  beginCommercialBreak(): void {
    if (this.commercialBreak || this.silenced) return;
    this.commercialBreak = true;
    this.preCommercialCombatMuted = this.combatMuted;
    this.combatMuted = true;
    this.wasLowPower = false;
    this.lowPowerPulseTimer = 0;
    this.sfx.stopCombat(0.12);
    this.crowd.park(0.2);
    this.announcer.interrupt(true);
    this.tracks.setLevel(0, 0.22);
    this.music.setDuck(0, 0.22);
  }

  /** Undo beginCommercialBreak: fade arena beds back in. */
  endCommercialBreak(): void {
    if (!this.commercialBreak) return;
    this.commercialBreak = false;
    this.combatMuted = this.preCommercialCombatMuted || this.audioPaused;
    if (this.silenced || this.audioPaused || this.musicHeld) return;
    this.crowd.unpark(0.5);
    this.crowd.setMurmur(this.inArena ? 0.16 : 0.11, 0.5);
    this.tracks.setLevel(1, 0.55);
    if (this.tracks.playing()) {
      // Streamed bed owns the room; keep procedural parked.
      this.music.setDuck(0, 0.08);
      this.music.setStreamed(true);
    } else {
      this.music.setDuck(1, 0.55);
      this.music.setStreamed(false);
    }
  }

  isCommercialBreak(): boolean {
    return this.commercialBreak;
  }

  /** Skip Vaelen's comedy death line; the elimination replay carries the moment. */
  suppressNextDeathAnnouncer(): void {
    this.suppressDeathLine = true;
  }

  /** Park arena beds for the death cinematic. No-op if PlayerDied already silenced. */
  beginDeathCinematic(): void {
    if (this.silenced) return;
    this.beginCommercialBreak();
  }

  endDeathCinematic(): void {
    this.endCommercialBreak();
  }

  /** Unpark arena beds after a continue revive. No-op if pause or a commercial owns audio. */
  resumeAfterContinue(): void {
    if (this.audioPaused || this.commercialBreak) return;
    this.crowd.unpark(0.4);
    this.crowd.setMurmur(this.inArena ? 0.16 : 0.11, 0.4);
    this.crowd.sting('cheer', 0.5, 0, true);
    this.resumeArenaAudio();
  }

  /** Park arena beds for the victory cinematic. Same mute as a commercial break. */
  beginVictoryCinematic(): void {
    if (this.silenced) return;
    this.beginCommercialBreak();
  }

  endVictoryCinematic(): void {
    this.endCommercialBreak();
  }

  /**
   * Leave the arena for the title screen: silence combat, park beds, and clear
   * flags so playMenuMusic / startIntroBed can run again.
   */
  returnToMenu(): void {
    this.silenceArena();
    this.inArena = false;
    this.leftMenu = false;
    this.welcomed = false;
    this.audioPaused = false;
  }

  /** Soft mute for the pause-menu toggle (master bus). */
  setMuted(muted: boolean): void {
    this.buses.master.gain.setTargetAtTime(muted ? 0.0001 : 0.9, this.buses.now, 0.05);
  }

  isMuted(): boolean {
    return this.buses.master.gain.value < 0.05;
  }

  private holdFloorMusic(): void {
    this.musicHeld = true;
    this.tracks.stop(0.28);
    this.music.stop(0.28);
  }

  private resumeArenaAudio(): void {
    this.silenced = false;
    this.combatMuted = false;
    this.musicHeld = false;
    this.bossQuietUntil = 0;
    this.tracks.setLevel(1, 0.3);
    if (!this.music.isRunning()) this.music.start();
    this.syncStreamedMusic();
  }

  /** True while the boss death crescendo owns the room and music must stay parked. */
  private bossQuiet(): boolean {
    return this.buses.now < this.bossQuietUntil;
  }

  /** Entrance finished: the landing hit is the downbeat the drum fill was driving to. */
  private onBossLanded(kind: number): void {
    const def = bossDef(kind);
    switch (def.body) {
      case BossBody.Stone:
        this.sfx.play(SfxId.BossStomp, 1.1);
        break;
      case BossBody.Goo:
        this.sfx.play(SfxId.BossSplat, 1.1);
        break;
      case BossBody.Leather:
        this.sfx.play(SfxId.BossWallHit, 0.8);
        break;
      default:
        this.sfx.play(SfxId.BossBlink, 1);
        break;
    }
  }

  private onBossImpact(impact: number): void {
    switch (impact as BossImpactKind) {
      case BossImpactKind.Land:
        // The landing itself is covered by BossLanded; this is the crater dust.
        this.sfx.play(SfxId.Shockwave, 0.7);
        break;
      case BossImpactKind.Stomp:
        this.sfx.play(SfxId.BossStomp);
        break;
      case BossImpactKind.Wall:
        this.sfx.play(SfxId.BossWallHit);
        break;
      case BossImpactKind.Screech:
        this.sfx.play(SfxId.BossScreech);
        break;
      case BossImpactKind.Splat:
        this.sfx.play(SfxId.BossSplat, 0.8);
        break;
      case BossImpactKind.Split:
        this.sfx.play(SfxId.BossSplit);
        break;
    }
  }

  /** Which streamed track the current room wants, or null for "procedural only". */
  private wantedTrack(): TrackId | null {
    const room = this.ctx.room;
    // Clear / walk: no bed. RoomEnter starts the next one so death + clear own the gap.
    if (
      room.phase === RoomPhase.Clear ||
      room.phase === RoomPhase.Transition ||
      room.phase === RoomPhase.Victory ||
      room.phase === RoomPhase.GameOver
    ) {
      return null;
    }
    const boss = room.bossState === BossState.Alive || (room.bossState === BossState.Dead && room.phase === RoomPhase.Fight);
    const id: TrackId = boss ? 'boss' : floorThemeDef(this.ctx.floor.floor).track;
    return this.tracks.has(id) ? id : null;
  }

  /** Streamed layer wins when its file exists; otherwise the procedural loop plays alone. */
  private syncStreamedMusic(): void {
    if (!this.inArena || this.musicHeld || this.silenced) return;
    const want = this.wantedTrack();
    if (want) {
      if (this.tracks.playing() !== want) this.tracks.play(want, this.tracks.playing() ? 1.8 : 1.2);
      this.music.setStreamed(true);
    } else {
      if (this.tracks.playing()) this.tracks.stop(1.2);
      // Keep procedural parked during the post-boss walk; unmute only when a bed is meant to play.
      const quiet =
        this.ctx.room.phase === RoomPhase.Clear ||
        this.ctx.room.phase === RoomPhase.Transition ||
        this.ctx.room.phase === RoomPhase.Victory ||
        this.ctx.room.phase === RoomPhase.GameOver;
      this.music.setStreamed(quiet);
    }
  }

  /**
   * Handoff into "GATES ARE LOCKED": the one curtain whoosh plus the arena loop.
   * Both boot paths (cinematic and `?intro=none`) call this exactly once; the
   * cinematic itself ends on the send-off line and plays no whoosh of its own.
   */
  async unlock(): Promise<void> {
    await this.buses.resume();
    this.inArena = true;
    this.leftMenu = true;
    this.combatMuted = false;
    this.musicHeld = false;
    this.silenced = false;
    this.sfx.play(SfxId.Curtain, 0.9);
    this.crowd.setMurmur(0.16, 0.6);
    this.crowd.sting('cheer', 0.75, 0.12, true);
    this.music.start();
    this.syncStreamedMusic();
  }

  destroy(): void {
    this.crowd.stopAll(0.05);
    this.music.stop();
    this.tracks.destroy();
    this.buses.destroy();
  }

  update(frameDt: number): void {
    const ctx = this.ctx;
    this.consumeEvents();
    this.announcer.update();
    this.crowd.update(frameDt);

    if (this.silenced) {
      this.sfx.setBeam(false, false);
      this.wasLowPower = false;
      this.lowPowerPulseTimer = 0;
      return;
    }

    if (this.audioPaused || this.commercialBreak) {
      this.sfx.setBeam(false, false);
      return;
    }

    if (!this.musicHeld && !this.bossQuiet()) {
      const monsters = ctx.pools.count(PoolKind.Imp) + ctx.pools.count(PoolKind.Golem) * 4;
      // The boss layer stays up through the death crescendo and drops on the room clear.
      this.music.setBoss(ctx.room.bossState === BossState.Alive || (ctx.room.bossState === BossState.Dead && ctx.room.phase === RoomPhase.Fight));
      this.music.setIntensity((monsters - 20) / 280);
      this.music.setThemeFromFloor(ctx.floor.floor);
      this.syncStreamedMusic();
    }

    if (this.combatMuted) {
      this.sfx.setBeam(false, false);
      this.wasLowPower = false;
      this.lowPowerPulseTimer = 0;
      return;
    }

    let beam = false;
    let oc = false;
    let playerLowPower = false;
    let playerCritical = false;
    const players = query(ctx.world, [Player, Weapon, Health]);
    for (let i = 0; i < players.length; i++) {
      const eid = players[i];
      if (Player.dead[eid]) continue;
      const frac = Health.hp[eid] / (Health.max[eid] || 1);
      if (frac <= Config.player.lowPowerFraction) {
        playerLowPower = true;
        if (frac <= Config.player.criticalPowerFraction) playerCritical = true;
        if (this.nearDeathArmed) {
          this.nearDeathArmed = false;
          this.crowd.sting('gasp', 0.75);
          this.announcer.say('near_death');
        }
      } else if (frac > 0.55) {
        this.nearDeathArmed = true;
      }
      if (!Player.firing[eid]) continue;
      if (weaponDef(Weapon.typeId[eid]).pattern !== FirePattern.Beam) continue;
      beam = true;
      if (Weapon.overcharge[eid] > 0) oc = true;
    }
    this.sfx.setBeam(beam, oc);

    if (playerLowPower && !this.silenced && !this.combatMuted && !this.audioPaused) {
      if (!this.wasLowPower) {
        this.wasLowPower = true;
        this.sfx.play(SfxId.LowPowerWarning);
        this.lowPowerPulseTimer = 0.25;
      }
      this.lowPowerPulseTimer -= frameDt;
      if (this.lowPowerPulseTimer <= 0) {
        const interval = playerCritical ? 0.65 : 1.15;
        this.lowPowerPulseTimer = interval;
        this.sfx.play(SfxId.Heartbeat, playerCritical ? 1.0 : 0.7);
      }
    } else {
      this.wasLowPower = false;
      this.lowPowerPulseTimer = 0;
    }
  }

  private consumeEvents(): void {
    if (this.commercialBreak) return;
    const ctx = this.ctx;
    const events = ctx.events;
    for (let i = 0; i < events.length; i++) {
      const e = events.peekAt(i);
      if (this.combatMuted && this.isCombatEvent(e.type)) continue;
      switch (e.type) {
        case GameEvent.Shot:
          this.onShot(e.b as WeaponId);
          break;
        case GameEvent.EnemyDied:
          if (e.b === EnemyKind.Boss) break;
          // Cinders have their own blast cue; the kill tick would just double it.
          if (e.b !== EnemyKind.Cinder) this.sfx.play(e.b === EnemyKind.Golem || e.b === EnemyKind.Brute || e.b === EnemyKind.Mimic ? SfxId.GolemKill : SfxId.Kill, 0.9);
          this.onKillLine(e.b as EnemyKind);
          break;
        case GameEvent.CinderBlast:
          this.sfx.play(SfxId.Explosion, 0.8);
          break;
        case GameEvent.MineArmed:
          this.sfx.play(SfxId.MineArmed, 0.75);
          break;
        case GameEvent.MineBlast:
          this.sfx.play(SfxId.Explosion, 1);
          if (Math.random() < MINE_LINE_CHANCE) this.announcer.say('mine_blast');
          break;
        case GameEvent.Cataclysm:
          this.sfx.play(SfxId.Cataclysm);
          this.sfx.play(SfxId.Shockwave, 0.95);
          this.crowd.sting('gasp', 0.85, 0, true);
          this.announcer.say('cataclysm');
          break;
        case GameEvent.BruteWall:
          this.sfx.play(SfxId.BossWallHit, 0.55);
          this.announcer.say('brute_wall');
          break;
        case GameEvent.MimicWake:
          this.sfx.play(SfxId.NewThreat, 0.9);
          this.crowd.sting('gasp', 0.7);
          this.announcer.say('mimic_wake');
          break;
        case GameEvent.MimicSlain:
          this.sfx.play(SfxId.Prize, 0.9);
          this.announcer.say('mimic_slain');
          break;
        case GameEvent.FloorModifier: {
          // Floor rule call lands after the gate slam and before the pack chatter.
          this.sfx.play(SfxId.OnAir, 0.7);
          const group = floorMod(e.a).voiceGroup;
          if (group) this.announcer.say(group);
          break;
        }
        case GameEvent.BossIncoming:
          // Last portal down: the floor track ducks under a one-second rune build and a
          // drum fill whose crash lands exactly where the boss appears. Vaelen cuts in
          // over any stale chatter with the arrival line.
          this.tracks.setLevel(0.3, 0.25);
          this.sfx.play(SfxId.BossIncoming);
          this.sfx.play(SfxId.BossDrumFill, 0.9);
          this.crowd.sting('gasp', 0.85, 0, true);
          this.crowd.setMurmur(0.18, 0.5);
          this.announcer.interrupt(true);
          this.announcer.say(bossDef(e.a).voiceGroup);
          break;
        case GameEvent.BossSpawn: {
          const def = bossDef(e.b);
          this.sfx.play(BOSS_ARRIVE_SFX[def.kind] ?? SfxId.BossArrive);
          this.crowd.sting('ooh', 0.7, 0.12);
          this.crowd.setMurmur(0.18, 0.4);
          // Boss track comes in at full level (syncStreamedMusic switches the file).
          this.tracks.setLevel(1, 0.5);
          this.music.setBoss(true);
          this.syncStreamedMusic();
          break;
        }
        case GameEvent.BossLanded:
          this.onBossLanded(e.b);
          break;
        case GameEvent.BossTelegraph:
          this.sfx.play(telegraphSfx(e.b), 0.9);
          break;
        case GameEvent.BossCast:
          this.sfx.play(e.b === BossPattern.Crown ? SfxId.BossSkullLaunch : SfxId.BossCast, 0.9);
          break;
        case GameEvent.BossImpact:
          this.onBossImpact(e.a);
          break;
        case GameEvent.BossSummon:
          this.sfx.play(SfxId.BossSummon, Math.min(1.5, 0.5 + e.b * 0.25));
          break;
        case GameEvent.BossHit:
          // Beams land every tick; the min gap in Sfx keeps this to a few per second.
          this.sfx.play(hurtSfx(ctx.room.bossKind), Math.min(1.05, 0.72 + e.b * 0.025));
          break;
        case GameEvent.BossHalf:
          this.sfx.play(SfxId.BossHalf);
          this.crowd.sting('gasp', 0.8, 0, true);
          this.announcer.say('boss_half');
          break;
        case GameEvent.BossBlink:
          this.sfx.play(SfxId.BossBlink, 0.8);
          break;
        case GameEvent.BossDeath: {
          // Park every music bed now. The death sample owns the room; the next
          // floor/boss track waits for RoomEnter so nothing stacks in the clear.
          this.holdFloorMusic();
          this.bossQuietUntil = this.buses.now + 30;
          const sample = BOSS_DEATH_SAMPLE[e.b as BossKind] ?? 'boss_death_lich';
          if (this.sfx.playSample(sample, 1.1)) {
            // Sample carries the character; keep a low rumble so the floor still shakes.
            this.sfx.playDeathRumble(0.75);
          } else {
            this.sfx.play(deathSfx(e.b));
          }
          this.announcer.interrupt(true);
          break;
        }
        case GameEvent.BossDown:
          // Final blast landed: dry hit, crowd eruption, then Vaelen calls it.
          this.sfx.play(SfxId.BossDownHit, 0.95, 0.05);
          this.crowd.sting('cheerBig', 1.05, 0.1, true);
          this.crowd.setMurmur(0.14, 0.8);
          this.announcer.clearQueue();
          this.announcer.say('boss_down');
          break;
        case GameEvent.SpawnerHit:
          this.sfx.play(SfxId.PortalHit, 0.1);
          break;
        case GameEvent.EnemyHit: {
          // The starter wand dumps this cue all night; keep it a tick, not a slap.
          const wand = e.a === WeaponId.Wand;
          const heavy = e.b === EnemyKind.Golem || e.b === EnemyKind.Brute || e.b === EnemyKind.Gargoyle || e.b === EnemyKind.Spitter || e.b === EnemyKind.Mimic;
          this.sfx.play(SfxId.Hit, wand ? 0.22 : heavy ? 0.85 : 0.7);
          break;
        }
        case GameEvent.SpawnerDestroyed:
          this.sfx.play(SfxId.PortalDown);
          if (e.b === 1) this.announcer.say('last_portal');
          else if (e.b > 1 && Math.random() < PORTAL_LINE_CHANCE) this.announcer.say('portal_down');
          break;
        case GameEvent.PlayerHit:
          this.sfx.play(SfxId.PlayerHurt, Math.min(1, e.b / 20));
          if (e.b >= 15) {
            this.crowd.sting('gasp', 0.7);
            this.announcer.say('heal_expensive');
          } else if (e.b >= 10) {
            this.crowd.sting('ooh', 0.55);
            if (Math.random() < HURT_LINE_CHANCE) this.announcer.say('gotta_hurt');
          } else if (Math.random() < HURT_LINE_CHANCE) this.announcer.say('gotta_hurt');
          break;
        case GameEvent.DragonBurn:
          this.sfx.play(SfxId.DragonBurn, 0.8);
          this.crowd.sting('ooh', 0.7);
          if (Math.random() < 0.4) this.announcer.say('dragon_burn');
          break;
        case GameEvent.DragonRoar:
          this.sfx.play(SfxId.DragonRoar, 0.9);
          this.announcer.say('dragon_roar');
          break;
        case GameEvent.KillStreak:
          this.sfx.play(e.a >= STREAK_TIER3_COMBO ? SfxId.Streak3 : e.a >= STREAK_TIER2_COMBO ? SfxId.Streak2 : SfxId.Streak);
          this.crowd.sting(e.a >= STREAK_TIER2_COMBO ? 'cheerBig' : 'cheer', e.a >= STREAK_TIER3_COMBO ? 1 : 0.75);
          if (Math.random() < STREAK_LINE_CHANCE) this.announcer.say('kill_streak');
          break;
        case GameEvent.GemGreed:
          this.announcer.say('gem_greed');
          break;
        case GameEvent.FloorCleared:
          // Previous floor/boss bed dies here; the next theme waits for RoomEnter.
          this.holdFloorMusic();
          this.sfx.play(SfxId.RoomClear);
          this.lastClearStingAt = this.buses.now;
          this.crowd.sting('applause', 0.75, 0.2, true);
          this.crowd.sting('cheer', 0.65, 0.45, true);
          this.announcer.say('floor_cleared');
          break;
        case GameEvent.RunWon:
          this.stopCombat();
          this.holdFloorMusic();
          this.announcer.interrupt(true);
          // Victory cinematic owns the big eruption after the sting + VO.
          this.crowd.sting('cheer', 0.55, 0.05);
          break;
        case GameEvent.PrizeWave: {
          const since = this.buses.now - this.lastClearStingAt;
          const delay = since < PRIZE_AFTER_CLEAR ? PRIZE_AFTER_CLEAR - Math.max(0, since) : 0;
          this.sfx.play(SfxId.Prize, 0.6, delay);
          this.crowd.sting('cheer', 0.55, delay);
          this.announcer.say('prize_wave');
          break;
        }
        case GameEvent.PlayerDied:
          this.stopCombat();
          this.crowd.park(0.18);
          this.crowd.sting('gasp', 0.85, 0, true);
          this.announcer.interrupt(true);
          this.sfx.play(SfxId.PlayerDeath);
          if (ctx.continuePrompt) break;
          this.silenced = true;
          if (this.suppressDeathLine) this.suppressDeathLine = false;
          else this.announcer.say('player_death');
          break;
        case GameEvent.PickupWeapon: {
          this.sfx.play(SfxId.PickupWeapon);
          const voice = weaponDef(e.b).voiceId;
          if (voice) this.announcer.say(voice);
          break;
        }
        case GameEvent.Overcharge:
          this.sfx.play(SfxId.Overcharge);
          if (Math.random() < 0.45) this.announcer.say('overcharge');
          break;
        case GameEvent.WeaponExpired:
          this.sfx.play(SfxId.WeaponExpired, 0.8);
          if (Math.random() < EXPIRED_LINE_CHANCE) this.announcer.say('weapon_expired');
          break;
        case GameEvent.PickupGem:
          this.sfx.play(SfxId.Gem, 0.8);
          break;
        case GameEvent.PickupHeart:
          this.sfx.play(SfxId.SupplyHeart);
          if (e.b > 0 && Math.random() < HEAL_LINE_CHANCE) this.announcer.say('heal');
          break;
        case GameEvent.PickupShield:
          this.sfx.play(SfxId.PickupShield);
          if (Math.random() < PICKUP_LINE_CHANCE) this.announcer.say('pickup_shield');
          break;
        case GameEvent.PickupSwift:
          this.sfx.play(SfxId.PickupSwift);
          if (Math.random() < PICKUP_LINE_CHANCE) this.announcer.say('pickup_swift');
          break;
        case GameEvent.OffhandHit:
          this.sfx.play(SfxId.OffhandHit, 0.7);
          break;
        case GameEvent.PlayerDash:
          this.sfx.play(SfxId.Dash, 0.85);
          break;
        case GameEvent.OffhandExpired:
        case GameEvent.SwiftExpired:
          this.sfx.play(SfxId.WeaponExpired, 0.45);
          if (Math.random() < BUFF_LINE_CHANCE) this.announcer.say('buff_expired');
          break;
        case GameEvent.SupplyDrop:
          this.sfx.play(SfxId.SupplyDrop, 0.8);
          if (e.b === -1) this.announcer.say('supply_heart');
          else if (e.b >= 0) this.announcer.say('supply_weapon');
          break;
        case GameEvent.PickupPrize:
          this.sfx.play(SfxId.Prize);
          this.crowd.sting('cheer', 0.5);
          if (Math.random() < PRIZE_LINE_CHANCE) this.announcer.say(PRIZE_LINES[e.b as PrizeKind] ?? 'prize_cabbage');
          break;
        case GameEvent.RoomEnter:
          this.resumeArenaAudio();
          this.sfx.play(SfxId.GateSlam);
          this.crowd.unpark(0.4);
          this.crowd.setMurmur(0.16, 0.5);
          this.music.accent();
          this.idleHeckles = 0;
          if (!this.welcomed) {
            this.welcomed = true;
            this.announcer.say('opening');
          } else {
            this.announcer.say(ROOM_ENTER_LINES[ctx.room.door] ?? 'room_enter');
          }
          break;
        case GameEvent.NewThreat: {
          this.sfx.play(SfxId.NewThreat, 0.8);
          const group = newThreatAnnouncerGroup(e.a as EnemyKind);
          if (group) this.announcer.say(group);
          break;
        }
        case GameEvent.RoomCleared:
          // Park beds with the combat mute. A late floor-track restart was stacking
          // on the death sample, the clear arp, and a still-running procedural loop.
          this.stopCombat();
          this.sfx.play(SfxId.RoomClear);
          this.sfx.play(SfxId.GateOpen, 0.85, GATE_OPEN_DELAY);
          this.lastClearStingAt = this.buses.now;
          if (ctx.room.door !== DoorKind.Exit && ctx.room.door !== DoorKind.Sanctum) {
            this.crowd.sting('cheer', 0.6, 0.12);
          }
          // A sanctum "clear" is just the gates lifting; no victory call for a nap.
          if (ctx.room.door !== DoorKind.Exit && ctx.room.door !== DoorKind.Sanctum) this.announcer.say('room_clear');
          break;
        case GameEvent.StallWarning:
          this.sfx.play(SfxId.Alarm, 0.8);
          this.announcer.say(e.a === 1 ? 'corner_camping' : 'stall_warning');
          break;
        case GameEvent.DragonSpawn:
          this.sfx.play(SfxId.DragonArrive);
          this.crowd.sting('gasp', 0.7);
          this.announcer.say('dragon_arrive');
          break;
        case GameEvent.PlayerIdle:
          if (this.silenced || this.combatMuted) break;
          if (this.idleHeckles < IDLE_HECKLES_PER_ROOM && this.announcer.say('idle')) {
            this.idleHeckles++;
          }
          break;
        case GameEvent.Shockwave:
          this.sfx.play(SfxId.Shockwave);
          break;
        case GameEvent.RoomNext:
          // Queued heckles about the room we just left are stale by the time the next one opens.
          this.announcer.clearQueue();
          this.sfx.play(SfxId.MinimapZoom, 0.8);
          break;
        default:
          break;
      }
    }
  }

  /** Combat one-shots and beds that must not leak into a clear, walk, or death. */
  private isCombatEvent(type: GameEvent): boolean {
    switch (type) {
      case GameEvent.Shot:
      case GameEvent.EnemyDied:
      case GameEvent.BossTelegraph:
      case GameEvent.BossCast:
      case GameEvent.BossHit:
      case GameEvent.BossHalf:
      case GameEvent.BossBlink:
      case GameEvent.BossDeath:
      case GameEvent.BossIncoming:
      case GameEvent.BossSpawn:
      case GameEvent.BossLanded:
      case GameEvent.BossImpact:
      case GameEvent.BossSummon:
      case GameEvent.BossDown:
      case GameEvent.EnemyHit:
      case GameEvent.SpawnerHit:
      case GameEvent.SpawnerDestroyed:
      case GameEvent.PlayerHit:
      case GameEvent.DragonBurn:
      case GameEvent.DragonRoar:
      case GameEvent.DragonSpawn:
      case GameEvent.KillStreak:
      case GameEvent.StallWarning:
      case GameEvent.Shockwave:
      case GameEvent.NewThreat:
      case GameEvent.SupplyDrop:
      case GameEvent.Overcharge:
      case GameEvent.WeaponExpired:
      case GameEvent.CinderBlast:
      case GameEvent.MineArmed:
      case GameEvent.MineBlast:
      case GameEvent.Cataclysm:
      case GameEvent.BruteWall:
      case GameEvent.MimicWake:
      case GameEvent.MimicSlain:
      case GameEvent.PlayerDash:
        return true;
      default:
        return false;
    }
  }

  /** Kill chatter is rationed: golems get a coin flip, small fry only every Nth kill. */
  private onKillLine(kind: EnemyKind): void {
    if (kind === EnemyKind.Golem || kind === EnemyKind.Brute) {
      if (Math.random() < GOLEM_LINE_CHANCE) this.announcer.say(kind === EnemyKind.Brute ? 'brute_down' : 'golem_down');
      return;
    }
    // The mimic has its own payout line.
    if (kind === EnemyKind.Mimic) return;
    this.killsSinceTaunt++;
    if (this.killsSinceTaunt < this.nextTauntAt) return;
    this.killsSinceTaunt = 0;
    this.nextTauntAt = rollKillTaunt();
    this.announcer.say('kill_taunt');
  }

  private onShot(weapon: WeaponId): void {
    if (weapon === WeaponId.HydraMortar) {
      this.sfx.play(SfxId.ShotFire);
      return;
    }
    if (weapon === WeaponId.Cataclysm) return;
    switch (weaponDef(weapon).pattern) {
      case FirePattern.Crescent:
        this.sfx.play(SfxId.ShotCleaver);
        break;
      case FirePattern.PhaseBolt:
        this.sfx.play(SfxId.ShotPhase);
        break;
      case FirePattern.Bolt:
        this.sfx.play(SfxId.ShotWand, weapon === WeaponId.Wand ? 0.32 : 0.8);
        break;
      default:
        break;
    }
  }
}
